import { defineStore } from "pinia";
import { ref, computed } from "vue";

import fieldValueService, {
  type GetFieldValuesResponse,
  type SyncDefaultFieldsResponse,
  type DefaultFieldsStatusResponse,
  type ViewsSyncStatusResponse,
  type SyncToViewsResponse,
} from "@/services/fieldValueService";

export const useFieldValueStore = defineStore("fieldValues", () => {
  // State
  const fieldValuesCache = ref<Map<string, GetFieldValuesResponse>>(new Map());
  const loading = ref(false);
  const error = ref<null | string>(null);
  const syncStatus = ref<DefaultFieldsStatusResponse | null>(null);
  const lastSyncResult = ref<null | SyncDefaultFieldsResponse>(null);
  const viewsSyncStatus = ref<null | ViewsSyncStatusResponse>(null);
  const lastViewsSyncResult = ref<null | SyncToViewsResponse>(null);

  // Bumped by $reset; a response from an older generation belongs to the previous user.
  let generation = 0;

  // Computed
  const getCacheKey = (entityType: string, entityId: string, campaignId: string) => {
    return `${entityType}-${entityId}-${campaignId}`;
  };

  // Actions
  async function fetchFieldValues(
    entityType: "CASE" | "CLIENT",
    entityId: string,
    campaignId: string,
    forceRefresh = false
  ): Promise<GetFieldValuesResponse> {
    const cacheKey = getCacheKey(entityType, entityId, campaignId);

    // Return from cache if available and not forcing refresh
    if (!forceRefresh && fieldValuesCache.value.has(cacheKey)) {
      return fieldValuesCache.value.get(cacheKey)!;
    }

    loading.value = true;
    error.value = null;
    const requestGeneration = generation;

    try {
      const response = await fieldValueService.getFieldValues(entityType, entityId, campaignId);
      // After a reset these values are the previous user's: neither cache nor hand them out.
      if (requestGeneration !== generation) return { campaignId, entityId, entityType, fields: [] };

      // Cache the response
      fieldValuesCache.value.set(cacheKey, response);

      return response;
    } catch (err) {
      if (requestGeneration === generation)
        error.value = err instanceof Error ? err.message : "Failed to fetch field values";
      throw err;
    } finally {
      if (requestGeneration === generation) loading.value = false;
    }
  }

  async function updateFieldValue(
    entityType: "CASE" | "CLIENT",
    entityId: string,
    fieldId: string,
    campaignId: string,
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any
  ): Promise<void> {
    loading.value = true;
    error.value = null;
    const requestGeneration = generation;

    try {
      await fieldValueService.setFieldValue({
        campaignId,
        entityId,
        entityType,
        fieldId,
        value,
      });

      // Invalidate cache for this entity
      const cacheKey = getCacheKey(entityType, entityId, campaignId);
      fieldValuesCache.value.delete(cacheKey);

      // Optionally refetch to update cache
      // Not after a reset: that refetch would fill the cleared store for the previous user.
      if (requestGeneration === generation)
        await fetchFieldValues(entityType, entityId, campaignId, true);
    } catch (err) {
      if (requestGeneration === generation)
        error.value = err instanceof Error ? err.message : "Failed to update field value";
      throw err;
    } finally {
      if (requestGeneration === generation) loading.value = false;
    }
  }

  async function bulkUpdateFieldValues(
    entityType: "CASE" | "CLIENT",
    entityId: string,
    campaignId: string,
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fieldValues: Record<string, any>
  ): Promise<void> {
    loading.value = true;
    error.value = null;
    const requestGeneration = generation;

    try {
      await fieldValueService.bulkSetFieldValues({
        campaignId,
        entityId,
        entityType,
        fieldValues,
      });

      // Invalidate cache
      const cacheKey = getCacheKey(entityType, entityId, campaignId);
      fieldValuesCache.value.delete(cacheKey);

      // Refetch to update cache
      // Not after a reset: that refetch would fill the cleared store for the previous user.
      if (requestGeneration === generation)
        await fetchFieldValues(entityType, entityId, campaignId, true);
    } catch (err) {
      if (requestGeneration === generation)
        error.value = err instanceof Error ? err.message : "Failed to update field values";
      throw err;
    } finally {
      if (requestGeneration === generation) loading.value = false;
    }
  }

  async function deleteFieldValue(
    fieldId: string,
    entityType: "CASE" | "CLIENT",
    entityId: string,
    campaignId: string
  ): Promise<void> {
    loading.value = true;
    error.value = null;
    const requestGeneration = generation;

    try {
      await fieldValueService.deleteFieldValue(fieldId, entityType, entityId);

      // Invalidate cache
      const cacheKey = getCacheKey(entityType, entityId, campaignId);
      fieldValuesCache.value.delete(cacheKey);
    } catch (err) {
      if (requestGeneration === generation)
        error.value = err instanceof Error ? err.message : "Failed to delete field value";
      throw err;
    } finally {
      if (requestGeneration === generation) loading.value = false;
    }
  }

  async function syncDefaultFields(
    campaignId?: string,
    dryRun = true
  ): Promise<SyncDefaultFieldsResponse> {
    loading.value = true;
    error.value = null;
    const requestGeneration = generation;

    try {
      const response = await fieldValueService.syncDefaultFields({
        campaignId,
        dryRun,
      });

      if (requestGeneration === generation) lastSyncResult.value = response;

      // If not a dry run, refresh sync status
      if (!dryRun) {
        // Not after a reset: that refetch would fill the cleared store for the previous user.
        if (requestGeneration === generation) await fetchDefaultFieldsStatus();
      }

      return response;
    } catch (err) {
      if (requestGeneration === generation)
        error.value = err instanceof Error ? err.message : "Failed to sync default fields";
      throw err;
    } finally {
      if (requestGeneration === generation) loading.value = false;
    }
  }

  async function fetchDefaultFieldsStatus(): Promise<DefaultFieldsStatusResponse> {
    loading.value = true;
    error.value = null;
    const requestGeneration = generation;

    try {
      const response = await fieldValueService.getDefaultFieldsStatus();
      if (requestGeneration === generation) syncStatus.value = response;
      return response;
    } catch (err) {
      if (requestGeneration === generation)
        error.value = err instanceof Error ? err.message : "Failed to fetch sync status";
      throw err;
    } finally {
      if (requestGeneration === generation) loading.value = false;
    }
  }

  async function fetchViewsSyncStatus(): Promise<ViewsSyncStatusResponse> {
    loading.value = true;
    error.value = null;
    const requestGeneration = generation;

    try {
      const response = await fieldValueService.getViewsSyncStatus();
      if (requestGeneration === generation) viewsSyncStatus.value = response;
      return response;
    } catch (err) {
      if (requestGeneration === generation)
        error.value = err instanceof Error ? err.message : "Failed to fetch views sync status";
      throw err;
    } finally {
      if (requestGeneration === generation) loading.value = false;
    }
  }

  async function syncToViews(viewIds: string[], dryRun = false): Promise<SyncToViewsResponse> {
    loading.value = true;
    error.value = null;
    const requestGeneration = generation;

    try {
      const response = await fieldValueService.syncToViews({
        dryRun,
        viewIds,
      });

      if (requestGeneration === generation) lastViewsSyncResult.value = response;

      // If not a dry run, refresh views sync status
      if (!dryRun) {
        // Not after a reset: that refetch would fill the cleared store for the previous user.
        if (requestGeneration === generation) await fetchViewsSyncStatus();
      }

      return response;
    } catch (err) {
      if (requestGeneration === generation)
        error.value = err instanceof Error ? err.message : "Failed to sync to views";
      throw err;
    } finally {
      if (requestGeneration === generation) loading.value = false;
    }
  }

  function clearCache(): void {
    fieldValuesCache.value.clear();
  }

  function clearCacheForEntity(entityType: string, entityId: string, campaignId: string): void {
    const cacheKey = getCacheKey(entityType, entityId, campaignId);
    fieldValuesCache.value.delete(cacheKey);
  }

  // Computed getters for easy access
  const campaignsNeedingSync = computed(() => {
    return syncStatus.value?.summary.campaignsNeedingSync || 0;
  });

  const hasUnsyncedCampaigns = computed(() => {
    return campaignsNeedingSync.value > 0;
  });

  /**
   * Drop every cached record. Pinia only gives options stores a working `$reset`; on a setup store
   * it's a no-op in production, so auth's clearAllStores (sign-out, or a magic-link account switch
   * with no page reload) used to leave the previous user's data here. Bumping `generation` stops a
   * request that was in flight at the reset from writing its response afterwards.
   */
  function $reset() {
    generation++;
    fieldValuesCache.value = new Map();
    loading.value = false;
    error.value = null;
    syncStatus.value = null;
    lastSyncResult.value = null;
    viewsSyncStatus.value = null;
    lastViewsSyncResult.value = null;
  }

  return {
    $reset,
    error,
    // State
    fieldValuesCache,
    lastSyncResult,
    lastViewsSyncResult,
    loading,
    syncStatus,
    viewsSyncStatus,

    // Computed
    campaignsNeedingSync,
    hasUnsyncedCampaigns,

    bulkUpdateFieldValues,
    clearCache,
    clearCacheForEntity,
    deleteFieldValue,
    fetchDefaultFieldsStatus,
    // Actions
    fetchFieldValues,
    fetchViewsSyncStatus,
    syncDefaultFields,
    syncToViews,
    updateFieldValue,
  };
});
